import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  selectOnboardingForRole,
  skipOnboarding,
  saveOnboarding,
  ONBOARDING_STEPS,
} from "../../slices/onboardingSlice";
import { useOnboardingRole } from "../../pages/Onboarding/hooks/useOnboardingRole";
import { getStepComponentsForRole } from "../../pages/Onboarding/config";
import OnboardingStepper from "../../pages/Onboarding/components/OnboardingStepper";

const LS_KEY = "ob_wizard_open";

const OnboardingWizard: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const role = useOnboardingRole();
  const onboarding = useAppSelector(selectOnboardingForRole(role));
  const steps = ONBOARDING_STEPS[role];
  const stepComponents = getStepComponentsForRole(role);

  // Collapse state persisted in localStorage
  const [isOpen, setIsOpen] = useState<boolean>(
    () => localStorage.getItem(LS_KEY) !== "false"
  );

  // Determine display step from URL param, falling back to Redux currentStep
  const obStepParam = searchParams.get("ob_step");
  const parsedParam = obStepParam !== null ? parseInt(obStepParam, 10) : NaN;
  const displayStep =
    !isNaN(parsedParam) && parsedParam >= 0 && parsedParam < steps.length
      ? parsedParam
      : onboarding.currentStep;

  // When Redux currentStep advances (auto-complete), sync ob_step param
  useEffect(() => {
    const next = onboarding.currentStep;
    if (!isNaN(parsedParam) && parsedParam !== next) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("ob_step", String(next));
          return p;
        },
        { replace: true }
      );
    }
  }, [onboarding.currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpen = () => {
    setIsOpen((prev) => {
      const next = !prev;
      localStorage.setItem(LS_KEY, String(next));
      return next;
    });
  };

  const handleSkip = () => {
    dispatch(skipOnboarding({ role }));
    dispatch(
      saveOnboarding({
        role,
        current_step: onboarding.currentStep,
        completed_steps: [...onboarding.completedSteps],
        is_complete: false,
        is_skipped: true,
      })
    );
    // Remove ob_step param and stay on current page
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("ob_step");
        return p;
      },
      { replace: true }
    );
  };

  // Don't render on the full-page onboarding route or when done/skipped
  if (
    location.pathname === "/onboarding" ||
    onboarding.isComplete ||
    onboarding.isSkipped
  ) {
    return null;
  }

  const stepNumber = displayStep + 1;
  const totalSteps = steps.length;

  // For Admin, step 0 (plan selection) is compulsory — hide skip
  const canSkip = role === "Admin" ? onboarding.currentStep > 0 : true;

  const StepComponent = stepComponents[displayStep];

  if (!isOpen) {
    return (
      <button
        onClick={toggleOpen}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-primary/90 transition-colors"
      >
        <span>Setup: Step {stepNumber} of {totalSteps}</span>
        <ChevronUp className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex w-[380px] flex-col rounded-xl border border-gray-200 bg-white shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-xl bg-primary px-4 py-3">
        <span className="text-sm font-semibold text-white">
          Setup Guide · Step {stepNumber}/{totalSteps}
        </span>
        <button
          onClick={toggleOpen}
          className="text-white/80 hover:text-white transition-colors"
          aria-label="Minimize wizard"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      </div>

      {/* Stepper */}
      <div className="px-4 pt-3">
        <OnboardingStepper
          steps={steps}
          currentStep={onboarding.currentStep}
          completedSteps={[...onboarding.completedSteps]}
        />
      </div>

      {/* Step content */}
      <div className="max-h-[340px] overflow-y-auto px-4 pb-2">
        {StepComponent && <StepComponent />}
      </div>

      {/* Footer */}
      {canSkip && (
        <div className="border-t border-gray-100 px-4 py-3 text-center">
          <button
            onClick={handleSkip}
            className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
          >
            Skip Onboarding
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingWizard;
